import { QueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { isOnline } from "@/lib/connectivity";

/**
 * Whether a failed query is worth retrying. Cold app starts (and a
 * cold App Runner backend) briefly surface network/timeout/5xx errors
 * on the very first authenticated request; without a backoff-retry
 * React Query latches that transient error and — with a multi-minute
 * staleTime and no refetch trigger — leaves the screen stuck on
 * "Request failed" until the component remounts. Riding those out with
 * a few backed-off retries self-heals the common case.
 *
 * Deterministic client errors (400/401/403/404/422) will never fix
 * themselves, so we don't waste round-trips on them. 401s are already
 * handled upstream by the axios interceptor (refresh + retry, else
 * logout), so they rarely reach here anyway.
 */
function isRetriableError(error: unknown): boolean {
  // Never retry while offline: the network isn't coming back mid-loop, so
  // retrying just stalls the UI behind backoff (up to ~14s) before `apiGet`
  // can serve the offline cache / the query settles. Fail fast → cache serves
  // instantly. `refetchOnReconnect` re-runs everything once we're back online.
  if (!isOnline()) return false;
  // Declined by the module-control guard (lib/module-request-guard.ts): the
  // school has that module off, and no amount of retrying changes that.
  if ((error as { code?: unknown } | null)?.code === "MODULE_DISABLED") return false;
  const status = (error as AxiosError)?.response?.status;
  if (status === undefined) return true; // no response = network / timeout / cold backend
  return status >= 500 || status === 408 || status === 429;
}

/** A 429 is retried once, after this long, when the server sent no usable Retry-After. */
export const RATE_LIMIT_DEFAULT_RETRY_MS = 2_000;
/** A Retry-After longer than this is not waited out by a query: it fails and the user retries. */
export const RATE_LIMIT_MAX_RETRY_MS = 30_000;

function isRateLimitError(error: unknown): boolean {
  return (error as AxiosError | null)?.response?.status === 429;
}

/** The server's `Retry-After` (whole seconds) in ms, or null when absent / not a number. */
function retryAfterMs(error: unknown): number | null {
  const headers = (error as AxiosError | null)?.response?.headers as
    | Record<string, unknown>
    | undefined;
  const raw = headers?.["retry-after"] ?? headers?.["Retry-After"];
  if (typeof raw !== "string" && typeof raw !== "number") return null;
  const seconds = Number(String(raw).trim());
  if (!Number.isFinite(seconds) || seconds < 0 || String(raw).trim() === "") return null;
  return Math.round(seconds * 1000);
}

/**
 * How long React Query waits before retry number `attempt` (1 = first retry).
 *
 * A 429 means the shared per-IP budget is spent; hammering it again on the
 * 1s/2s/4s ladder only deepens the hole (every colleague on the office network
 * is in the same bucket). So it waits out the server's `Retry-After` when sent,
 * else 2 s. Everything else keeps the backoff `min(1000 * 2^attempt, 8000)`.
 */
export function retryDelayFor(error: unknown, attempt: number): number {
  if (isRateLimitError(error)) return retryAfterMs(error) ?? RATE_LIMIT_DEFAULT_RETRY_MS;
  return Math.min(1000 * 2 ** attempt, 8000);
}

/**
 * Whether React Query retries after `failureCount` failures (0 = the first
 * attempt just failed). Network / 5xx / 408 keep up to three retries; a 429
 * gets exactly one — and none when the server asks for a wait longer than
 * RATE_LIMIT_MAX_RETRY_MS, since retrying earlier is guaranteed to 429 again.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (!isRetriableError(error)) return false;
  if (isRateLimitError(error)) {
    return failureCount < 1 && (retryAfterMs(error) ?? 0) <= RATE_LIMIT_MAX_RETRY_MS;
  }
  return failureCount < 3;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: shouldRetryQuery,
      retryDelay: (attempt, error) => retryDelayFor(error, attempt),
      // CRITICAL for offline (kiosk especially): React Query's default
      // networkMode "online" PAUSES a query the moment `navigator.onLine`
      // is false — the queryFn never runs. That silently disables our
      // entire offline story, because the offline-cache read lives INSIDE
      // `apiGet`'s catch block and only executes when the queryFn actually
      // runs. "always" makes React Query invoke the queryFn regardless of
      // connectivity, so `apiGet` gets to serve the IndexedDB cache when the
      // network is down. Without this, warmed classes/rosters/teachers never
      // appear offline unless they were fetched while still online.
      networkMode: "always",
    },
    mutations: {
      // Same reason: default "online" pauses mutations offline, so
      // `apiPostOrQueue` never runs and the kiosk "Save Attendance" tap
      // hangs on a spinner with no "saved offline" feedback. "always" lets
      // the mutationFn run and queue the write to the outbox immediately.
      networkMode: "always",
    },
  },
});
